import { useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { buildWhatsAppLink } from "@/lib/constants";

const ROLES = [
  "Member",
  "Service Provider",
  "Business / MSME",
  "Institution / NGO",
  "Partner",
  "Volunteer",
  "Investor / Supporter",
  "Other",
];

const WhatsAppForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    location: "",
    role: "Member",
    message: "",
    consent: false,
  });
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.consent) {
      setError("Please share your name, phone, and accept the consent to continue.");
      return;
    }
    const text = `Namaste Believers Common,

I'd like to register my interest in joining as a ${form.role}.

Name: ${form.name}
Phone: ${form.phone}
Email: ${form.email || "—"}
Location: ${form.location || "—"}

Note:
${form.message || "—"}

(Sent via believerscommon.org)`;

    const url = buildWhatsAppLink(text);
    window.open(url, "_blank", "noopener,noreferrer");
    setSubmitted(true);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8"
      data-testid="whatsapp-interest-form"
    >
      <div className="md:col-span-1">
        <label className="eyebrow-slate block mb-2">01 · Full name</label>
        <input
          className="bc-input"
          placeholder="Your full name"
          value={form.name}
          onChange={set("name")}
          data-testid="form-name"
        />
      </div>
      <div className="md:col-span-1">
        <label className="eyebrow-slate block mb-2">02 · Phone</label>
        <input
          className="bc-input"
          placeholder="+91 …"
          value={form.phone}
          onChange={set("phone")}
          data-testid="form-phone"
        />
      </div>
      <div className="md:col-span-1">
        <label className="eyebrow-slate block mb-2">03 · Email</label>
        <input
          className="bc-input"
          type="email"
          placeholder="you@email.com"
          value={form.email}
          onChange={set("email")}
          data-testid="form-email"
        />
      </div>
      <div className="md:col-span-1">
        <label className="eyebrow-slate block mb-2">04 · City, State</label>
        <input
          className="bc-input"
          placeholder="Bengaluru, Karnataka"
          value={form.location}
          onChange={set("location")}
          data-testid="form-location"
        />
      </div>
      <div className="md:col-span-1">
        <label className="eyebrow-slate block mb-2">05 · I am interested as</label>
        <select
          className="bc-select"
          value={form.role}
          onChange={set("role")}
          data-testid="form-role"
        >
          {ROLES.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
      </div>
      <div className="md:col-span-1">
        <label className="eyebrow-slate block mb-2">06 · Message</label>
        <input
          className="bc-input"
          placeholder="A short note (optional)"
          value={form.message}
          onChange={set("message")}
          data-testid="form-message"
        />
      </div>

      <div className="md:col-span-2 flex items-start gap-3 pt-2">
        <button
          type="button"
          onClick={() => setForm((f) => ({ ...f, consent: !f.consent }))}
          className={`relative mt-1 w-5 h-5 border ${
            form.consent ? "bg-[#D6B25E] border-[#D6B25E]" : "border-white/30 bg-transparent"
          } flex items-center justify-center transition-colors`}
          data-testid="form-consent"
          aria-checked={form.consent}
          role="checkbox"
        >
          {form.consent && <Check className="w-3.5 h-3.5 text-[#0A0D14]" strokeWidth={3} />}
        </button>
        <p className="text-[13px] leading-relaxed text-[#6F7787] max-w-xl">
          I agree to be contacted by Believers Common for onboarding, verification,
          and community-related purposes. My information will not be sold or
          shared beyond what is necessary for the requested service.
        </p>
      </div>

      {error && (
        <div
          className="md:col-span-2 text-[13px] text-[#D6B25E] tracking-wide"
          data-testid="form-error"
        >
          {error}
        </div>
      )}

      <div className="md:col-span-2 flex flex-wrap items-center gap-4 pt-4">
        <button type="submit" className="btn-primary" data-testid="form-submit">
          Send via WhatsApp
          <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <span className="text-[12px] tracking-[0.22em] uppercase text-[#6F7787]">
          Opens chat with +91 96864 54696
        </span>
        {submitted && (
          <span
            className="text-[12px] tracking-[0.22em] uppercase text-[#58D2C7]"
            data-testid="form-success-indicator"
          >
            · Chat launched
          </span>
        )}
      </div>
    </form>
  );
};

export default WhatsAppForm;
